import { useEffect, useMemo, useState } from 'react';

import {
  mergeAvatarRuntimeConfig,
  type AvatarRuntimeConfig
} from '../avatarRuntimeConfig';

export type AvatarRuntimeConfigLoadStatus = 'idle' | 'loading' | 'ready' | 'error';
export type AvatarRuntimeConfigSource = 'runtime-service' | 'cache' | 'defaults' | 'disabled';

/**
 * Where an embed's saved settings come from (Aug 2026 three-level redesign):
 *
 *  - `'platform'` — resolve the tenant chain through the runtime facade
 *    (`/api/avatar/config/:projectToken`, i.e. tenant → system default).
 *    Requires a projectToken. This is the pre-redesign behaviour.
 *  - `'system'`  — resolve ONLY the admin-managed system defaults via
 *    `/api/avatar/settings/resolve?modelId=`. Works tokenless; the default
 *    for embeds with no projectToken.
 *  - `'custom'`  — no self-fetch at all; the host supplies everything via
 *    `initialConfig` / `hostOverrides`.
 *
 * hostOverrides / initialConfig stay the highest-precedence layer in every
 * mode.
 */
export type AvatarSettingsSource = 'system' | 'platform' | 'custom';

export interface AvatarRuntimeConfigResponse {
  config: Record<string, unknown> | null;
  source?: string;
  fetchedAt?: string;
  updatedAt?: string;
  version?: string | number;
}

export interface AvatarRuntimeConfigResult {
  status: AvatarRuntimeConfigLoadStatus;
  config: AvatarRuntimeConfig | null;
  source: AvatarRuntimeConfigSource | string;
  error: Error | null;
  fetchedAt?: string;
  updatedAt?: string;
  version?: string | number;
}

export interface UseAvatarRuntimeConfigOptions {
  projectToken?: string | null;
  modelId?: string | null;
  runtimeUrl?: string | null;
  enabled?: boolean;
  hostOverrides?: Record<string, unknown> | null;
  ttlMs?: number;
  /**
   * Explicit settings source. When omitted: `'platform'` if a projectToken
   * is present, otherwise `'system'` (tokenless embeds reach the
   * admin-managed system defaults).
   */
  settingsSource?: AvatarSettingsSource | null;
}

export function resolveSettingsSource(
  settingsSource: AvatarSettingsSource | null | undefined,
  projectToken: string | null | undefined
): AvatarSettingsSource {
  if (settingsSource === 'system' || settingsSource === 'platform' || settingsSource === 'custom') {
    return settingsSource;
  }
  return projectToken?.trim() ? 'platform' : 'system';
}

interface CacheEntry {
  expiresAt: number;
  response: AvatarRuntimeConfigResponse;
}

const DEFAULT_TTL_MS = 15_000;
const cache = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<AvatarRuntimeConfigResponse>>();

function trimTrailingSlash(value: string): string {
  return value.replace(/\/+$/, '');
}

export function resolveDefaultRuntimeUrl(): string {
  if (typeof window === 'undefined') return '';
  const { hostname, origin } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:5075';
  }
  return `${origin.replace(/\/+$/, '')}/runtime-api`;
}

function resolveRuntimeUrl(runtimeUrl?: string | null): string {
  return trimTrailingSlash(runtimeUrl?.trim() || resolveDefaultRuntimeUrl());
}

function buildCacheKey(runtimeUrl: string, projectToken: string, modelId?: string | null): string {
  return `${runtimeUrl}::${projectToken}::${modelId?.trim() || ''}`;
}

function buildAvatarConfigUrl(
  runtimeUrl: string,
  projectToken: string | null,
  modelId?: string | null
): string {
  const cleanModelId = modelId?.trim();
  if (!projectToken) {
    // Tokenless / system-level resolve: only the admin-managed system
    // defaults (plus the model-registry baseline) contribute.
    const url = new URL(`${runtimeUrl}/api/avatar/settings/resolve`);
    if (cleanModelId) {
      url.searchParams.set('modelId', cleanModelId);
    }
    return url.toString();
  }
  const url = new URL(`${runtimeUrl}/api/avatar/config/${encodeURIComponent(projectToken)}`);
  if (cleanModelId) {
    url.searchParams.set('modelId', cleanModelId);
  }
  return url.toString();
}

export async function fetchAvatarRuntimeConfig(
  options: Pick<UseAvatarRuntimeConfigOptions, 'projectToken' | 'modelId' | 'runtimeUrl' | 'ttlMs' | 'settingsSource'> & { signal?: AbortSignal }
): Promise<{ response: AvatarRuntimeConfigResponse; source: AvatarRuntimeConfigSource | string }> {
  const projectToken = options.projectToken?.trim();
  const runtimeUrl = resolveRuntimeUrl(options.runtimeUrl);
  const settingsSource = resolveSettingsSource(options.settingsSource, projectToken);
  if (!runtimeUrl || settingsSource === 'custom') {
    return { response: { config: null }, source: 'disabled' };
  }
  if (settingsSource === 'platform' && !projectToken) {
    return { response: { config: null }, source: 'disabled' };
  }
  if (settingsSource === 'system' && !options.modelId?.trim()) {
    // System-level settings are per-model; without a model there is
    // nothing to resolve.
    return { response: { config: null }, source: 'disabled' };
  }

  const effectiveToken = settingsSource === 'system' ? null : projectToken ?? null;
  const ttlMs = options.ttlMs ?? DEFAULT_TTL_MS;
  const key = buildCacheKey(runtimeUrl, effectiveToken ?? '__system__', options.modelId);
  const cached = cache.get(key);
  if (cached && cached.expiresAt > Date.now()) {
    return { response: cached.response, source: 'cache' };
  }

  let pending = inflight.get(key);
  if (!pending) {
    pending = fetch(buildAvatarConfigUrl(runtimeUrl, effectiveToken, options.modelId), {
      headers: { Accept: 'application/json' },
      signal: options.signal
    }).then(async (response) => {
      if (!response.ok) {
        throw new Error(`Avatar config request failed (${response.status})`);
      }
      const body = await response.json() as AvatarRuntimeConfigResponse;
      const normalized: AvatarRuntimeConfigResponse = {
        config: body.config && typeof body.config === 'object' && !Array.isArray(body.config)
          ? body.config as Record<string, unknown>
          : null,
        source: body.source,
        fetchedAt: body.fetchedAt,
        updatedAt: body.updatedAt,
        version: body.version
      };
      cache.set(key, { response: normalized, expiresAt: Date.now() + ttlMs });
      return normalized;
    }).finally(() => {
      inflight.delete(key);
    });
    inflight.set(key, pending);
  }

  return { response: await pending, source: 'runtime-service' };
}

export function useAvatarRuntimeConfig({
  projectToken,
  modelId,
  runtimeUrl,
  enabled = true,
  hostOverrides,
  ttlMs,
  settingsSource
}: UseAvatarRuntimeConfigOptions): AvatarRuntimeConfigResult {
  const [state, setState] = useState<AvatarRuntimeConfigResult>(() => ({
    status: enabled ? 'loading' : 'idle',
    config: mergeAvatarRuntimeConfig({ modelId, hostOverrides }),
    source: enabled ? 'defaults' : 'disabled',
    error: null
  }));

  const defaults = useMemo(
    () => mergeAvatarRuntimeConfig({ modelId, hostOverrides }),
    [hostOverrides, modelId]
  );

  useEffect(() => {
    const cleanProjectToken = projectToken?.trim();
    const effectiveSource = resolveSettingsSource(settingsSource, cleanProjectToken);
    const canFetch =
      effectiveSource === 'platform'
        ? Boolean(cleanProjectToken)
        : effectiveSource === 'system'
          ? Boolean(modelId?.trim())
          : false;
    if (!enabled || !canFetch) {
      setState({
        status: 'idle',
        config: defaults,
        source: enabled ? 'defaults' : 'disabled',
        error: null
      });
      return;
    }

    const controller = new AbortController();
    setState((previous) => ({
      ...previous,
      status: previous.config ? 'ready' : 'loading',
      config: previous.config ?? defaults,
      source: previous.config ? previous.source : 'defaults',
      error: null
    }));

    fetchAvatarRuntimeConfig({
      projectToken: cleanProjectToken,
      modelId,
      runtimeUrl,
      ttlMs,
      settingsSource: effectiveSource,
      signal: controller.signal
    })
      .then(({ response, source }) => {
        if (controller.signal.aborted) return;
        setState({
          status: 'ready',
          config: mergeAvatarRuntimeConfig({
            modelId,
            savedConfig: response.config,
            hostOverrides
          }),
          source: source === 'runtime-service' ? (response.source ?? source) : source,
          error: null,
          fetchedAt: response.fetchedAt,
          updatedAt: response.updatedAt,
          version: response.version
        });
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        setState({
          status: 'error',
          config: defaults,
          source: 'defaults',
          error: error instanceof Error ? error : new Error(String(error))
        });
      });

    return () => controller.abort();
  }, [defaults, enabled, hostOverrides, modelId, projectToken, runtimeUrl, settingsSource, ttlMs]);

  return state;
}

export default useAvatarRuntimeConfig;
